import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';
import {
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';

import AppSnackbar from '../../common/AppSnackbar';
import { formatDate } from '../../common/formatter';
import SearchBox from '../../common/SearchBox';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateCardOver from '../../common/page-template/PageTemplateCardOver';
import PageTemplateHeader from '../../common/page-template/PageTemplateHeader';
import { useFindSignupList } from '../../login/Signup.hooks';

const SignupListPage: React.FC = () => {
  const history = useHistory();
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [direction, setDirection] = useQueryParam('direction', StringParam);
  const [search = '', setSearch] = useQueryParam('search', StringParam);
  const { signupList, isLoading, error, get } = useFindSignupList();

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };

  useEffect(() => {
    get({ page, limit, order, direction, search });
  }, [page, limit, order, direction, search, get]);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>Sign Up List</h2>
        <SearchBox value={search} onSearch={setSearch} />
      </PageTemplateHeader>
      <PageTemplateCardOver>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={order === 'signup.companyName'}
                    direction={getSortDirection('signup.companyName')}
                    onClick={() => handleSort('signup.companyName')}
                  >
                    Company Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'user.firstName'}
                    direction={getSortDirection('user.firstName')}
                    onClick={() => handleSort('user.firstName')}
                  >
                    First Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'user.lastName'}
                    direction={getSortDirection('user.lastName')}
                    onClick={() => handleSort('user.lastName')}
                  >
                    Last Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'user.email'}
                    direction={getSortDirection('user.email')}
                    onClick={() => handleSort('user.email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'signup.created'}
                    direction={getSortDirection('signup.created')}
                    onClick={() => handleSort('signup.created')}
                  >
                    Created Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'signup.modified'}
                    direction={getSortDirection('signup.modified')}
                    onClick={() => handleSort('signup.modified')}
                  >
                    Last Modified
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7} padding='none'>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {signupList?.data.map(signup => (
                <TableRow
                  hover
                  key={signup.id}
                  onClick={() => {
                    history.push(`/portal/admin/signups/${signup.id}`);
                  }}
                >
                  <TableCell>{signup.companyName}</TableCell>
                  <TableCell>{signup.user.firstName}</TableCell>
                  <TableCell>{signup.user.lastName}</TableCell>
                  <TableCell>{signup.user.email}</TableCell>
                  <TableCell>{formatDate(signup.created)}</TableCell>
                  <TableCell>{formatDate(signup.modified)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component='div'
          count={signupList?.total || 0}
          rowsPerPage={limit}
          page={page}
          onPageChange={(_, page) => setPage(page)}
          onRowsPerPageChange={event => {
            setLimit(+event.target.value);
            setPage(0);
          }}
        />
      </PageTemplateCardOver>

      <AppSnackbar severity='error' open={!!error}>
        {error?.message}
      </AppSnackbar>
    </PageTemplate>
  );
};

export default SignupListPage;
